import type {
  ApprovePayrollRunInput,
  CreatePayrollRunInput,
  ListPayrollRunsQuery,
  PayrollRunListResponse,
  PayrollRunResponse,
  PayrollRunStatus,
  VoidPayrollRunInput,
} from '@kora/types';
import { apiDownload, apiRequest } from './client.js';

/**
 * Payroll Runs API. Typed wrappers around /v1/payroll-runs* endpoints.
 * Contract is in apps/api/src/routes/payroll-runs.ts + schemas in @kora/types.
 */

// ---------------------------------------------------------------------------
// GET /v1/payroll-runs — keyset-paginated list
// ---------------------------------------------------------------------------

export interface ListPayrollRunsArgs {
  status?: PayrollRunStatus;
  cursor?: string;
  limit?: number;
}

export function listPayrollRuns(
  args: ListPayrollRunsArgs = {},
): Promise<PayrollRunListResponse> {
  const params = new URLSearchParams();
  if (args.status) params.set('status', args.status);
  if (args.cursor) params.set('cursor', args.cursor);
  if (args.limit) params.set('limit', String(args.limit));
  const qs = params.toString();
  return apiRequest<PayrollRunListResponse>(
    `/v1/payroll-runs${qs ? `?${qs}` : ''}`,
  );
}

// Accept the query type even if not used — keeps type inference stable
// across call sites that pass a validated ListPayrollRunsQuery object.
export type { ListPayrollRunsQuery };

// ---------------------------------------------------------------------------
// GET /v1/payroll-runs/:id
// ---------------------------------------------------------------------------

export function getPayrollRun(
  id: string,
  opts: { includePayslips?: boolean } = {},
): Promise<PayrollRunResponse> {
  const qs = opts.includePayslips ? '?includePayslips=true' : '';
  return apiRequest<PayrollRunResponse>(
    `/v1/payroll-runs/${encodeURIComponent(id)}${qs}`,
  );
}

// ---------------------------------------------------------------------------
// POST /v1/payroll-runs — create draft run (snapshot assembled server-side)
// ---------------------------------------------------------------------------

export function createPayrollRun(
  input: CreatePayrollRunInput,
): Promise<PayrollRunResponse> {
  return apiRequest<PayrollRunResponse>('/v1/payroll-runs', {
    method: 'POST',
    body: input,
  });
}

// ---------------------------------------------------------------------------
// POST /v1/payroll-runs/:id/calculate — 202 Accepted + Location header
// ---------------------------------------------------------------------------

export function calculatePayrollRun(id: string): Promise<PayrollRunResponse> {
  return apiRequest<PayrollRunResponse>(
    `/v1/payroll-runs/${encodeURIComponent(id)}/calculate`,
    { method: 'POST' },
  );
}

// ---------------------------------------------------------------------------
// POST /v1/payroll-runs/:id/approve
// ---------------------------------------------------------------------------

export function approvePayrollRun(
  id: string,
  input: ApprovePayrollRunInput,
): Promise<PayrollRunResponse> {
  return apiRequest<PayrollRunResponse>(
    `/v1/payroll-runs/${encodeURIComponent(id)}/approve`,
    { method: 'POST', body: input },
  );
}

// ---------------------------------------------------------------------------
// POST /v1/payroll-runs/:id/void
// ---------------------------------------------------------------------------

export function voidPayrollRun(
  id: string,
  input: VoidPayrollRunInput,
): Promise<PayrollRunResponse> {
  return apiRequest<PayrollRunResponse>(
    `/v1/payroll-runs/${encodeURIComponent(id)}/void`,
    { method: 'POST', body: input },
  );
}

// ---------------------------------------------------------------------------
// DELETE /v1/payroll-runs/:id — drafts only
// ---------------------------------------------------------------------------

/**
 * Hard-delete a draft payroll run. Only allowed when status=draft;
 * the server returns 409 otherwise and the caller should show a
 * message. Returns void (server replies 204).
 */
export function deletePayrollRun(id: string): Promise<void> {
  return apiRequest<void>(`/v1/payroll-runs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// GET /v1/payroll-runs/:runId/payslips/:payslipId/pdf — download payslip PDF
// ---------------------------------------------------------------------------

/**
 * Trigger a browser download of a single-page payslip PDF. Filename
 * comes from the server's Content-Disposition header (format:
 * `payslip-EMP-001-2026-04-01-v1.pdf`); the fallback only applies if
 * the header is unexpectedly missing.
 *
 * Returns when the download has been triggered. Throws ApiError on
 * non-2xx responses.
 */
export function downloadPayslipPdf(runId: string, payslipId: string): Promise<void> {
  return apiDownload(
    `/v1/payroll-runs/${encodeURIComponent(runId)}/payslips/${encodeURIComponent(payslipId)}/pdf`,
    'payslip.pdf',
  );
}
